import { DarkTheme, DefaultTheme } from 'expo-router';

// Flavrly palette
export const flavrlyGreen = {
  50: '#f0ffeb',
  100: '#deffd4',
  200: '#bcffa7',
  300: '#93fc70',
  400: '#7CE050',
  500: '#9ACD32',
  600: '#3daa00',
  700: '#32810a',
  800: '#2D6815',
  900: '#172811',
  950: '#0E160C',
} as const;

export const neutral = {
  50: '#F2F0EB',
  100: '#E8E5DF',
  200: '#D4D0C8',
  300: '#A8A49C',
  400: '#706C64',
  500: '#585450',
  600: '#3A3835',
  700: '#2E2C28',
  800: '#262420',
  900: '#151311',
  950: '#080706',
} as const;

export const functional = {
  error: '#E05555',
  errorMuted: 'rgba(224, 85, 85, 0.12)',
  success: '#9ACD32',
  successMuted: 'rgba(154, 205, 50, 0.15)',
  warning: '#E5A830',
  warningMuted: 'rgba(229, 168, 48, 0.12)',
  info: '#5B8DEF',
  infoMuted: 'rgba(91, 141, 239, 0.12)',
  coral: '#E8735A',
};

export const ratings = {
  skip:    { color: '#585450', bg: 'rgba(88, 84, 80, 0.10)' },
  ok:      { color: '#A8A49C', bg: 'rgba(168, 164, 156, 0.10)' },
  tryIt:   { color: '#9ACD32', bg: 'rgba(154, 205, 50, 0.10)' },
  mustTry: { color: '#D25E34', bg: 'rgba(124, 224, 80, 0.10)' },
};

// Unified theme definition supporting light/dark modes
export const createTheme = (colorScheme: 'light' | 'dark') => ({
  colors: {
    // Brand colors (static)
    flavrly: flavrlyGreen,
    neutral,
    functional,
    ratings,

    // Dynamic colors (change based on mode)
    primary: colorScheme === 'dark' ? '#9ACD32' : '#3daa00',
    secondary: colorScheme === 'dark' ? neutral[800] : neutral[600],
    tertiary: colorScheme === 'dark' ? neutral[700] : neutral[300],
    background: colorScheme === 'dark' ? neutral[950] : neutral[50],
    surface: colorScheme === 'dark' ? neutral[900] : neutral[100],
    surfaceSecondary: colorScheme === 'dark' ? neutral[800] : neutral[200],
    surfaceHover: colorScheme === 'dark' ? neutral[700] : neutral[200],
    text: colorScheme === 'dark' ? neutral[50] : neutral[900],
    textMuted: colorScheme === 'dark' ? neutral[300] : neutral[500],
    textSecondary: colorScheme === 'dark' ? neutral[400] : neutral[100],
    textTertiary: colorScheme === 'dark' ? neutral[300] : neutral[500],
    border: colorScheme === 'dark' ? '#2A2826' : neutral[200],
    borderVisible: colorScheme === 'dark' ? neutral[600] : neutral[200],

    // Tab/Navigation colors
    tabBarBackground: colorScheme === 'dark' ? neutral[900] : neutral[100],
    tabIconDefault: colorScheme === 'dark' ? neutral[300] : neutral[500],
    tabIconSelected: colorScheme === 'dark' ? 'white' : 'black',

    // Semantic colors
    error: '#E05555',
    success: '#9ACD32',
    warning: '#E5A830',
    info: '#5B8DEF',
    coral: '#E8735A',
    card: colorScheme === 'dark' ? neutral[900] : neutral[100],
  },

  // Design tokens
  spacing: {
    xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 40,
  },
  borderRadius: {
    none: 0, sm: 4, md: 8, lg: 8, xl: 12, full: 9999,
  },
  fonts: {
    main: 'Franie-Regular',
    mono: 'SpaceMono-Regular',
    system: 'Inter',
  },
  typography: {
    heading: { fontSize: 24, fontWeight: 'bold' as const, lineHeight: 32 },
    body: { fontSize: 16, fontWeight: 'normal' as const, lineHeight: 24 },
    caption: { fontSize: 14, fontWeight: 'normal' as const, lineHeight: 20 },
    button: { fontSize: 16, fontWeight: '600' as const, lineHeight: 24 },
  },
});

// Create React Navigation themes that use your colors
export const createNavigationTheme = (colorScheme: 'light' | 'dark') => {
  const theme = createTheme(colorScheme);

  if (colorScheme === 'dark') {
    return {
      ...DarkTheme,
      colors: {
        ...DarkTheme.colors,
        primary: '#9ACD32',
        background: '#080706',
        text: '#F2F0EB',
        border: '#2A2826',
        notification: '#E05555',
      },
    };
  }

  return {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      primary: theme.colors.primary,
      background: '#F2F0EB',
      card: theme.colors.tabBarBackground,
      text: '#1E1C19',
      border: '#D4D0C8',
      notification: '#E05555',
    },
  };
};

export type AppTheme = ReturnType<typeof createTheme>;
